import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import {
  Box, Card, CardContent, Typography, Stack, Button, Avatar, Chip, Skeleton,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import { AvisoDeLeitura } from "../components/AvisoDeLeitura";
import { useTenant } from "../context/TenantContext";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";

interface NotifEvent {
  id: string; level: "success" | "warning" | "error" | "info";
  title: string; detail: string; at?: string;
  kind: string; ref_id?: string;
}
interface NotifResponse { tenant: string; events: NotifEvent[]; stale: boolean; error?: string; }

const META: Record<NotifEvent["level"], { color: "success" | "error" | "warning" | "info"; Icon: any }> = {
  success: { color: "success", Icon: CheckCircleRoundedIcon },
  error:   { color: "error",   Icon: ErrorRoundedIcon },
  warning: { color: "warning", Icon: WarningAmberRoundedIcon },
  info:    { color: "info",    Icon: InfoRoundedIcon },
};

const READ_KEY = "platform-v2:notif-read";

function loadRead(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(READ_KEY) || "[]"));
  } catch { return new Set(); }
}
function saveRead(s: Set<string>) {
  localStorage.setItem(READ_KEY, JSON.stringify([...s]));
}

function friendlyDate(v?: string): string {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return v; }
}

export function Notifications() {
  const { tenant } = useTenant();
  const api = useApi();
  const [read, setRead] = useState<Set<string>>(() => loadRead());

  const q = useQuery({
    queryKey: ["notifications", tenant],
    queryFn: () => api.call<NotifResponse>("/api/notifications?limit=30"),
    enabled: !!tenant, staleTime: 30_000,
  });

  const events = q.data?.events ?? [];
  const unread = useMemo(() => events.filter((e) => !read.has(e.id)).length, [events, read]);

  function markAll() {
    const newRead = new Set(read);
    events.forEach((e) => newRead.add(e.id));
    setRead(newRead);
    saveRead(newRead);
  }
  function markOne(id: string) {
    const newRead = new Set(read);
    newRead.add(id);
    setRead(newRead);
    saveRead(newRead);
  }

  return (
    <>
      <PageHeader
        eyebrow="Central"
        title="Notificações"
        subtitle={<>Eventos de <strong>{tenant ?? "?"}</strong> · {events.length} eventos · {unread} não {unread === 1 ? "lido" : "lidos"}</>}
        action={
          <Button variant="outlined" startIcon={<DoneAllRoundedIcon />}
            onClick={markAll} disabled={unread === 0 || events.length === 0}>
            Marcar tudo como lido
          </Button>
        }
      />

      {q.data?.stale && <AvisoDeLeitura erro={q.data.error} oQue="as notificações" />}

      <Stack spacing={1.5}>
        {q.isLoading && Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Skeleton variant="circular" width={36} height={36} />
              <Box sx={{ flex: 1 }}><Skeleton width="70%" /><Skeleton width="40%" /></Box>
            </Stack>
          </Card>
        ))}
        {!q.isLoading && events.length === 0 && (
          <Card variant="outlined" sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">Sem notificações para este tenant nos últimos 14 dias.</Typography>
          </Card>
        )}
        {events.map((n) => {
          const M = META[n.level] ?? META.info;
          const isRead = read.has(n.id);
          return (
            <Card
              key={n.id}
              variant="outlined"
              sx={{
                borderLeft: 4,
                borderLeftColor: `${M.color}.main`,
                opacity: isRead ? 0.7 : 1,
                transition: "all 0.15s",
                cursor: isRead ? "default" : "pointer",
                "&:hover": !isRead ? { transform: "translateY(-1px)", boxShadow: "0 8px 16px rgba(2,26,44,0.08)" } : {},
              }}
              onClick={() => markOne(n.id)}
            >
              <CardContent sx={{ py: 2 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Avatar sx={{ bgcolor: `${M.color}.light`, color: `${M.color}.main`, width: 36, height: 36 }}>
                    <M.Icon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: isRead ? 400 : 600 }}>{n.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{n.detail}</Typography>
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>{friendlyDate(n.at)}</Typography>
                    {n.kind === "report_send" && n.ref_id && !isRead && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          component={RouterLink}
                          to={`/relatorios/${encodeURIComponent(n.ref_id)}`}
                          clickable
                          label="Ver relatório"
                          size="small"
                          color={M.color as any}
                          variant="outlined"
                          icon={<LaunchRoundedIcon sx={{ fontSize: "14px !important" }} />}
                          onClick={(e: any) => e.stopPropagation()}
                        />
                      </Box>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </>
  );
}
